import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayersIcon, UsersIcon, AlertTriangleIcon, ClockIcon,
  PlusIcon, SettingsIcon, ArrowUpIcon, BarChart3Icon,
  RefreshCwIcon,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useToast } from "@/hooks/use-toast";

interface Queue {
  id: number;
  name: string;
  level: string;
  description?: string;
  member_count: number;
  open_ticket_count: number;
  sla_hours?: number;
  auto_assign: boolean;
  is_active: boolean;
}

interface QueueMetric {
  level: string;
  open_tickets: number;
  sla_breached: number;
  avg_age_hours: number;
}

interface EscalationRule {
  id: number;
  name: string;
  ticket_type?: string;
  priority?: string;
  from_queue_level: string;
  to_queue_level: string;
  trigger_after_hours: number;
  sla_threshold_pct: number;
  is_active: boolean;
}

const LEVEL_COLORS: Record<string, string> = {
  N1: "bg-blue-100 text-blue-800 border-blue-200",
  N2: "bg-purple-100 text-purple-800 border-purple-200",
  N3: "bg-orange-100 text-orange-800 border-orange-200",
};

const QueueManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [queues, setQueues] = useState<Queue[]>([]);
  const [metrics, setMetrics] = useState<QueueMetric[]>([]);
  const [rules, setRules] = useState<EscalationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewQueue, setShowNewQueue] = useState(false);
  const [showNewRule, setShowNewRule] = useState(false);
  const [newQueue, setNewQueue] = useState({ name: "", level: "N1", description: "", sla_hours: "", auto_assign: false });
  const [newRule, setNewRule] = useState({
    name: "", ticket_type: "", priority: "", from_queue_level: "N1",
    to_queue_level: "N2", trigger_after_hours: "4", sla_threshold_pct: "80",
  });

  const token = getToken();
  const user = getUser() ? JSON.parse(getUser()!) : null;
  const isInternal = user && (user.role === "admin" || user.role === "internal" || user.role === "analyst");

  useEffect(() => {
    if (!isInternal) { navigate("/"); return; }
    loadAll();
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, mRes, rRes] = await Promise.all([
        fetch(getApiEndpoint("/api/queues"), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(getApiEndpoint("/api/queues/metrics"), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(getApiEndpoint("/api/queues/escalation/rules"), { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (qRes.ok) { const d = await qRes.json(); setQueues(d.queues || []); }
      if (mRes.ok) { const d = await mRes.json(); setMetrics(d.metrics || []); }
      if (rRes.ok) { const d = await rRes.json(); setRules(d.rules || []); }
    } catch (err) {
      toast({ title: "Erro", description: "Erro ao carregar filas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleCreateQueue = async () => {
    if (!newQueue.name || !newQueue.level) {
      toast({ title: "Campos obrigatórios", description: "Nome e nível são necessários", variant: "destructive" });
      return;
    }
    const res = await fetch(getApiEndpoint("/api/queues"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...newQueue, sla_hours: newQueue.sla_hours ? Number(newQueue.sla_hours) : null }),
    });
    if (res.ok) {
      toast({ title: "Fila criada", description: `Fila "${newQueue.name}" criada com sucesso` });
      setShowNewQueue(false);
      setNewQueue({ name: "", level: "N1", description: "", sla_hours: "", auto_assign: false });
      loadAll();
    } else {
      toast({ title: "Erro", description: "Não foi possível criar a fila", variant: "destructive" });
    }
  };

  const handleCreateRule = async () => {
    if (!newRule.name) {
      toast({ title: "Nome obrigatório", description: "Informe um nome para a regra", variant: "destructive" });
      return;
    }
    const res = await fetch(getApiEndpoint("/api/queues/escalation/rules"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ...newRule,
        ticket_type: newRule.ticket_type || null,
        priority: newRule.priority || null,
        trigger_after_hours: Number(newRule.trigger_after_hours),
        sla_threshold_pct: Number(newRule.sla_threshold_pct),
      }),
    });
    if (res.ok) {
      toast({ title: "Regra criada", description: "Regra de escalonamento criada" });
      setShowNewRule(false);
      setNewRule({ name: "", ticket_type: "", priority: "", from_queue_level: "N1", to_queue_level: "N2", trigger_after_hours: "4", sla_threshold_pct: "80" });
      loadAll();
    } else {
      toast({ title: "Erro", description: "Não foi possível criar a regra", variant: "destructive" });
    }
  };

  const toggleRule = async (rule: EscalationRule) => {
    const res = await fetch(getApiEndpoint(`/api/queues/escalation/rules/${rule.id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ is_active: !rule.is_active }),
    });
    if (res.ok) loadAll();
  };

  const totalOpen = metrics.reduce((s, m) => s + m.open_tickets, 0);
  const totalBreached = metrics.reduce((s, m) => s + m.sla_breached, 0);

  if (!isInternal) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      <Header />
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <LayersIcon className="h-6 w-6 text-blue-600" /> Gestão de Filas
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              Gerencie filas N1/N2/N3 e regras de escalonamento automático
            </p>
          </div>
          <Button onClick={loadAll} variant="outline" size="sm" className="gap-2">
            <RefreshCwIcon className="h-4 w-4" /> Atualizar
          </Button>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Chamados Abertos", value: totalOpen, icon: LayersIcon, color: "blue" },
            { label: "SLA Vencidos", value: totalBreached, icon: AlertTriangleIcon, color: "red" },
            { label: "Filas Ativas", value: queues.filter(q => q.is_active).length, icon: UsersIcon, color: "green" },
            { label: "Regras de Escalonamento", value: rules.filter(r => r.is_active).length, icon: ArrowUpIcon, color: "purple" },
          ].map((m, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{m.label}</p>
                    <p className={`text-2xl font-bold text-${m.color}-600`}>{loading ? "—" : m.value}</p>
                  </div>
                  <m.icon className={`h-8 w-8 text-${m.color}-200`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="queues">
          <TabsList className="mb-4">
            <TabsTrigger value="queues">Filas</TabsTrigger>
            <TabsTrigger value="metrics">Métricas por Nível</TabsTrigger>
            <TabsTrigger value="escalation">Escalonamento Automático</TabsTrigger>
          </TabsList>

          {/* ── Filas ── */}
          <TabsContent value="queues">
            <div className="flex justify-end mb-3">
              {(user?.role === "admin" || user?.role === "internal") && (
                <Button onClick={() => setShowNewQueue(true)} size="sm" className="gap-2">
                  <PlusIcon className="h-4 w-4" /> Nova Fila
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {queues.map((q) => (
                <Card key={q.id} className={`border ${!q.is_active ? "opacity-60" : ""}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-base">{q.name}</CardTitle>
                      <Badge className={`${LEVEL_COLORS[q.level]} border text-xs`}>{q.level}</Badge>
                    </div>
                    {q.description && <p className="text-xs text-gray-500 mt-1">{q.description}</p>}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1 text-gray-600">
                        <UsersIcon className="h-3.5 w-3.5" />
                        <span>{q.member_count} membro{q.member_count !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-600">
                        <LayersIcon className="h-3.5 w-3.5" />
                        <span>{q.open_ticket_count} abertos</span>
                      </div>
                      {q.sla_hours && (
                        <div className="flex items-center gap-1 text-gray-600">
                          <ClockIcon className="h-3.5 w-3.5" />
                          <span>SLA: {q.sla_hours}h</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Badge variant={q.auto_assign ? "default" : "outline"} className="text-xs px-1.5 py-0">
                          {q.auto_assign ? "Auto-distribuição" : "Manual"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!loading && queues.length === 0 && (
                <div className="col-span-full text-center text-gray-400 py-12">
                  <LayersIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>Nenhuma fila cadastrada. Crie a primeira fila para começar.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Métricas ── */}
          <TabsContent value="metrics">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {metrics.map((m) => (
                <Card key={m.level}>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Badge className={`${LEVEL_COLORS[m.level]} border`}>{m.level}</Badge>
                      Métricas
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Chamados abertos</span>
                      <span className="font-semibold">{m.open_tickets}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">SLA vencidos</span>
                      <span className={`font-semibold ${m.sla_breached > 0 ? "text-red-600" : "text-green-600"}`}>
                        {m.sla_breached}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Idade média</span>
                      <span className="font-semibold">{m.avg_age_hours}h</span>
                    </div>
                    {m.open_tickets > 0 && (
                      <div className="pt-2">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>SLA vencidos</span>
                          <span>{Math.round((m.sla_breached / m.open_tickets) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${m.sla_breached / m.open_tickets > 0.3 ? "bg-red-500" : "bg-green-500"}`}
                            style={{ width: `${Math.min((m.sla_breached / m.open_tickets) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ── Escalonamento ── */}
          <TabsContent value="escalation">
            <div className="flex justify-end mb-3">
              {(user?.role === "admin" || user?.role === "internal") && (
                <Button onClick={() => setShowNewRule(true)} size="sm" className="gap-2">
                  <PlusIcon className="h-4 w-4" /> Nova Regra
                </Button>
              )}
            </div>
            <div className="space-y-3">
              {rules.map((rule) => (
                <Card key={rule.id} className={!rule.is_active ? "opacity-60" : ""}>
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{rule.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {rule.from_queue_level} → {rule.to_queue_level} •{" "}
                        {rule.ticket_type ? `Tipo: ${rule.ticket_type}` : "Todos os tipos"} •{" "}
                        {rule.priority ? `Prioridade: ${rule.priority}` : "Todas as prioridades"}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="font-semibold text-orange-600">{rule.trigger_after_hours}h</p>
                        <p className="text-xs text-gray-400">Após abertura</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-red-600">{rule.sla_threshold_pct}%</p>
                        <p className="text-xs text-gray-400">SLA consumido</p>
                      </div>
                      {(user?.role === "admin" || user?.role === "internal") && (
                        <Button
                          variant={rule.is_active ? "outline" : "default"}
                          size="sm"
                          onClick={() => toggleRule(rule)}
                        >
                          {rule.is_active ? "Desativar" : "Ativar"}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!loading && rules.length === 0 && (
                <div className="text-center text-gray-400 py-12">
                  <ArrowUpIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>Nenhuma regra de escalonamento configurada.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog: Nova Fila */}
      <Dialog open={showNewQueue} onOpenChange={setShowNewQueue}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nova Fila</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Nome *</label>
              <Input value={newQueue.name} onChange={e => setNewQueue(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Suporte N1 - Atendimento" />
            </div>
            <div>
              <label className="text-sm font-medium">Nível *</label>
              <Select value={newQueue.level} onValueChange={v => setNewQueue(p => ({ ...p, level: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="N1">N1 — Suporte básico</SelectItem>
                  <SelectItem value="N2">N2 — Suporte técnico</SelectItem>
                  <SelectItem value="N3">N3 — Especialistas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Descrição</label>
              <Input value={newQueue.description} onChange={e => setNewQueue(p => ({ ...p, description: e.target.value }))} placeholder="Descrição da fila" />
            </div>
            <div>
              <label className="text-sm font-medium">SLA padrão (horas)</label>
              <Input type="number" value={newQueue.sla_hours} onChange={e => setNewQueue(p => ({ ...p, sla_hours: e.target.value }))} placeholder="Ex: 8" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewQueue(false)}>Cancelar</Button>
            <Button onClick={handleCreateQueue}>Criar Fila</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nova Regra */}
      <Dialog open={showNewRule} onOpenChange={setShowNewRule}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nova Regra de Escalonamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Nome *</label>
              <Input value={newRule.name} onChange={e => setNewRule(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Incidente Crítico N1→N2" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm font-medium">Tipo de ticket</label>
                <Select value={newRule.ticket_type || "all"} onValueChange={v => setNewRule(p => ({ ...p, ticket_type: v === "all" ? "" : v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="incident">Incidente</SelectItem>
                    <SelectItem value="request">Requisição</SelectItem>
                    <SelectItem value="problem">Problema</SelectItem>
                    <SelectItem value="change">Mudança</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Prioridade</label>
                <Select value={newRule.priority || "all"} onValueChange={v => setNewRule(p => ({ ...p, priority: v === "all" ? "" : v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="critical">Crítica</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="low">Baixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm font-medium">De</label>
                <Select value={newRule.from_queue_level} onValueChange={v => setNewRule(p => ({ ...p, from_queue_level: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="N1">N1</SelectItem>
                    <SelectItem value="N2">N2</SelectItem>
                    <SelectItem value="N3">N3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Para</label>
                <Select value={newRule.to_queue_level} onValueChange={v => setNewRule(p => ({ ...p, to_queue_level: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="N2">N2</SelectItem>
                    <SelectItem value="N3">N3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm font-medium">Após (horas)</label>
                <Input type="number" value={newRule.trigger_after_hours} onChange={e => setNewRule(p => ({ ...p, trigger_after_hours: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium">% SLA consumido</label>
                <Input type="number" value={newRule.sla_threshold_pct} onChange={e => setNewRule(p => ({ ...p, sla_threshold_pct: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRule(false)}>Cancelar</Button>
            <Button onClick={handleCreateRule}>Criar Regra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default QueueManagement;
